import React, { useState } from 'react';
import {
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { ExpandMore, HelpOutline } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Tabs } from 'new/elements/Tabs/Tabs';
import CopyButton from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/CopyButton';
import styles from './ApiServerDocumentation.module.scss';
import { useResourceApiKey } from 'new/hooks/useResourceApiKey';
import {
  generateCurlExample,
  hasEndpointConfiguration,
  getHttpMethodColor,
} from 'js/lib/ApiServerUtils';
import ApiServerParameterTable from 'js/components/data_sources/cards/configViews/ApiServerParameterTable';

interface ApiServerDocumentationProps {
  data?: any;
}

export const ApiServerDocumentation = ({ data }: ApiServerDocumentationProps) => {
  const [activeTab, setActiveTab] = useState('documentation');
  const [expandedContext, setExpandedContext] = useState(false);
  const sourceApiKey = useResourceApiKey(data?.data_source_id);
  const { url, api_key } = sourceApiKey;
  const { flow_node_id: flowNodeId, endpoint_spec: endpointSpec } = data;

  const displayCurlExample = generateCurlExample(endpointSpec, url, api_key, true);
  const copyCurlExample = generateCurlExample(endpointSpec, url, api_key, false);

  const tabs = [
    { value: 'documentation', label: 'Endpoint Spec Documentation' },
    { value: 'curl', label: 'cURL Examples' },
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleContextToggle = () => {
    setExpandedContext(!expandedContext);
  };

  return (
    <Stack gap="16px">
      <Stack gap="8px" className={styles.apiServerDocumentation}>
        {/* Expandable Context */}
        <Accordion
          expanded={expandedContext}
          onChange={handleContextToggle}
          className={styles.accordionItem}
        >
          <AccordionSummary
            expandIcon={<ExpandMore className={styles.accordionSummaryIcon} />}
            className={styles.accordionSummary}
          >
            <Box className={styles.accordionTitle}>
              <HelpOutline className={styles.accordionTitleIcon} sx={{ mr: 1 }} />
              <Typography variant="subtitle2" className={styles.accordionTitleText}>
                What is an API Server Flow?
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={styles.accordionDetails}>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
              API Server flows allow you to create RESTful APIs from your data flows, providing
              endpoints that can be consumed by external applications.
              <br />
              How it works:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Your flow becomes a RESTful API endpoint" />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Configure request parameters, headers, and response formats" />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Support various HTTP methods (GET, POST, PUT, DELETE)" />
              </ListItem>
              <ListItem sx={{ py: 0, px: 0 }}>
                <ListItemText primary="• Includes request validation, parameter mapping, and error handling" />
              </ListItem>
            </List>
            <Typography variant="subtitle2">Endpoint Configuration</Typography>
            <table className={styles.docTable}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>method</code>
                  </td>
                  <td>string</td>
                  <td>HTTP method (GET, POST, PUT, DELETE, etc.)</td>
                </tr>
                <tr>
                  <td>
                    <code>route</code>
                  </td>
                  <td>string</td>
                  <td>API endpoint path (e.g., /api/customers)</td>
                </tr>
                <tr>
                  <td>
                    <code>headers</code>
                  </td>
                  <td>array</td>
                  <td>Request headers configuration</td>
                </tr>
                <tr>
                  <td>
                    <code>path_params</code>
                  </td>
                  <td>array</td>
                  <td>URL path parameters</td>
                </tr>
                <tr>
                  <td>
                    <code>query_params</code>
                  </td>
                  <td>array</td>
                  <td>URL query parameters</td>
                </tr>
                <tr>
                  <td>
                    <code>body</code>
                  </td>
                  <td>object</td>
                  <td>Request body schema (for POST/PUT requests)</td>
                </tr>
              </tbody>
            </table>
          </AccordionDetails>
        </Accordion>

        <Tabs tabs={tabs} value={activeTab} onChange={handleTabChange} />

        <Stack gap="12px">
          {activeTab === 'documentation' && (
            <>
              {endpointSpec && (
                <>
                  <Stack gap="8px" marginTop="8px" direction="row" alignItems="center">
                    <Chip
                      label={endpointSpec.method}
                      color={getHttpMethodColor(endpointSpec.method) as any}
                      variant="outlined"
                    />
                    <Typography variant="body2">
                      {endpointSpec.route || 'No route configured'}
                    </Typography>
                  </Stack>
                  {hasEndpointConfiguration(endpointSpec) ? (
                    <Stack gap="8px">
                      <ApiServerParameterTable
                        params={endpointSpec.headers || []}
                        title="Headers"
                      />
                      <ApiServerParameterTable
                        params={endpointSpec.path_params || []}
                        title="Path Parameters"
                      />
                      <ApiServerParameterTable
                        params={endpointSpec.query_params || []}
                        title="Query Parameters"
                      />
                      <ApiServerParameterTable
                        body={endpointSpec.body}
                        title="Request Body"
                        subtitle="application/json"
                      />
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2">No configured endpoint specification</Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'curl' && (
            <>
              <div className={styles.tabDescription}>
                {/* <Typography variant="subtitle2">cURL Examples</Typography> */}
                <Typography variant="bodyDefault">
                  Use these ready-to-test cURL example to interact with your API Server flow.
                  Replace payload values with actual data based on the Endpoint Specification.
                </Typography>
              </div>

              <div className={styles.codeContainer}>
                <Stack justifyContent="flex-end" direction="row" alignItems="center" gap={2}>
                  <CopyButton title="Copy cURL" toCopy={copyCurlExample} />
                </Stack>
                <SyntaxHighlighter language="bash" style={a11yDark}>
                  {displayCurlExample}
                </SyntaxHighlighter>
              </div>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
